import React, { useEffect } from 'react';
import { Route, createBrowserRouter, createRoutesFromElements, RouterProvider, Navigate, useLocation } from 'react-router-dom';
import { App as AntApp } from 'antd';
import { SponsorProvider } from './context/SponsorContext';
import { ProfileProvider } from './context/ProfileContext';
import { AdminAuthProvider } from './context/AdminAuthContext';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminVerifications from './pages/admin/AdminVerifications';
import AdminEmailTemplates from './pages/admin/AdminEmailTemplates';
import AdminPayments from './pages/admin/AdminPayments';
import AdminPlans from './pages/admin/AdminPlans';
import AdminBrandPartnership from './pages/admin/AdminBrandPartnership';
import AdminMfa from './pages/admin/AdminMfa';
import AdminSettings from './pages/admin/AdminSettings';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Mfa from './pages/Mfa';
import Signup from './pages/Signup';
import SocialCallback from './pages/SocialCallback';
import MessageFromCEO from './pages/MessageFromCEO';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import TermsConditions from './pages/TermsConditions';
import ReturnRefundPolicy from './pages/ReturnRefundPolicy';
import Pricing from './pages/Pricing';
import ContactUs from './pages/ContactUs';
import Dashboard from './pages/Dashboard';
import Matches from './pages/Matches';
import Request from './pages/Request';
import Shortlist from './pages/Shortlist';
import Search from './pages/Search';
import ChangePassword from './pages/ChangePassword';
import Settings from './pages/Settings';
import Notification from './pages/Notification';
import Inbox from './pages/Inbox';
import Profile from './pages/Profile';
import AddProfile from './pages/AddProfile';
import EditProfile from './pages/EditProfile';
import PaymentReturn from './pages/PaymentReturn';
import PaymentHistory from './pages/PaymentHistory';
import SubscriptionManagement from './pages/SubscriptionManagement';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import BiodataUpload from './pages/BiodataUpload';
import NotFound from './pages/NotFound';
import BlockedProfiles from './pages/BlockedProfiles';
import BrowseProfiles from './pages/BrowseProfiles';
import AdvancedSearch from './pages/AdvancedSearch';

// Auth flag lives in localStorage so it survives page refresh.
const isAuthenticated = () => !!localStorage.getItem('user_id');
const isAdminAuthenticated = () => !!localStorage.getItem('admin_name');

// Admin protected route (reads from localStorage directly so it works outside context)
const AdminProtectedRoute = ({ element }) => {
	const location = useLocation();
	if (isAdminAuthenticated()) return element;
	return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />;
};

// Sync logout across tabs via the localStorage 'storage' event.
const setupLogoutSync = () => {
	window.addEventListener('storage', (e) => {
		if (e.key === 'logout') {
			localStorage.removeItem('user_id');
			localStorage.removeItem('first_name');
			localStorage.removeItem('last_name');
			window.location.href = '/';
		}
	});
};

// Protected Route Component
const ProtectedRoute = ({ element }) => {
	const location = useLocation();
	if (isAuthenticated()) return element;
	return <Navigate to="/login" state={{ redirectTo: location.pathname + location.search }} replace />;
};

const router = createBrowserRouter(
	createRoutesFromElements(
		<>
			{/* Public Routes */}
			<Route index element={<Landing />} />
			<Route path="/login" element={<Login />} />
			<Route path="/auth/callback/:provider" element={<SocialCallback />} />
			<Route path="/multi-factor-authentication" element={<Mfa />} />
			<Route path="/signup" element={<Signup />} />
			<Route path="/message-from-ceo" element={<MessageFromCEO />} />
			<Route path="/privacy-policy" element={<PrivacyPolicy />} />
			<Route path="/cookie-policy" element={<CookiePolicy />} />
			<Route path="/terms-conditions" element={<TermsConditions />} />
			<Route path="/return-refund-policy" element={<ReturnRefundPolicy />} />
			<Route path="/pricing" element={<Pricing />} />
			<Route path="/contact-us" element={<ContactUs />} />
			<Route path="/forgot-password" element={<ForgotPassword />} />
			<Route path="/reset-password" element={<ResetPassword />} />
			<Route path="/upload-biodata" element={<BiodataUpload />} />
			<Route path="/profiles" element={<BrowseProfiles />} />
			<Route path="/payment/return" element={<PaymentReturn />} />

			{/* Protected Routes */}
			<Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
			<Route path="/my-profile" element={<ProtectedRoute element={<Dashboard />} />} />
			<Route path="/partner-preferences" element={<ProtectedRoute element={<Dashboard />} />} />
			<Route path="/manage-photos" element={<ProtectedRoute element={<Dashboard />} />} />
			<Route path="/manage-profiles" element={<ProtectedRoute element={<Dashboard />} />} />
			<Route path="/verifications" element={<ProtectedRoute element={<Dashboard />} />} />
			<Route path="/account-settings" element={<ProtectedRoute element={<Settings />} />} />
			<Route path="/matches" element={<ProtectedRoute element={<Matches />} />} />
			<Route path="/requests/sent" element={<ProtectedRoute element={<Request />} />} />
			<Route path="/requests/received" element={<ProtectedRoute element={<Request />} />} />
			<Route path="/requests/accepted" element={<ProtectedRoute element={<Request />} />} />
			<Route path="/requests/rejected" element={<ProtectedRoute element={<Request />} />} />
			<Route path="/requests/cancelled" element={<ProtectedRoute element={<Request />} />} />
			<Route path="/shortlists/by-me" element={<ProtectedRoute element={<Shortlist />} />} />
			<Route path="/shortlists/by-others" element={<ProtectedRoute element={<Shortlist />} />} />
			<Route path="/search/by-id" element={<ProtectedRoute element={<Search />} />} />
			<Route path="/search/advanced" element={<ProtectedRoute element={<AdvancedSearch />} />} />
			<Route path="/inbox" element={<ProtectedRoute element={<Inbox />} />} />
			<Route path="/notifications" element={<ProtectedRoute element={<Notification />} />} />
			<Route path="/change-password" element={<ProtectedRoute element={<ChangePassword />} />} />
			<Route path="/profile" element={<ProtectedRoute element={<Profile />} />} />
			<Route path="/add-profile" element={<ProtectedRoute element={<AddProfile />} />} />
			<Route path="/edit-profile" element={<ProtectedRoute element={<EditProfile />} />} />
			<Route path="/payment-history" element={<ProtectedRoute element={<PaymentHistory />} />} />
			<Route path="/subscription-management" element={<ProtectedRoute element={<SubscriptionManagement />} />} />
			<Route path="/blocked-profiles" element={<ProtectedRoute element={<BlockedProfiles />} />} />

			{/* Admin Portal */}
			<Route path="/admin/login" element={<AdminLogin />} />
			<Route path="/admin/mfa" element={<AdminMfa />} />
			<Route path="/admin/dashboard" element={<AdminProtectedRoute element={<AdminDashboard />} />} />
			<Route path="/admin/users" element={<AdminProtectedRoute element={<AdminUsers />} />} />
			<Route path="/admin/users/:id" element={<AdminProtectedRoute element={<AdminUserDetail />} />} />
			<Route path="/admin/verifications" element={<AdminProtectedRoute element={<AdminVerifications />} />} />
			<Route path="/admin/email-templates" element={<AdminProtectedRoute element={<AdminEmailTemplates />} />} />
			<Route path="/admin/payments" element={<AdminProtectedRoute element={<AdminPayments />} />} />
			<Route path="/admin/plans" element={<AdminProtectedRoute element={<AdminPlans />} />} />
			<Route path="/admin/brand-partnership" element={<AdminProtectedRoute element={<AdminBrandPartnership />} />} />
			<Route path="/admin/settings" element={<AdminProtectedRoute element={<AdminSettings />} />} />
			<Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

			{/* 404 Route */}
			<Route path="*" element={<NotFound />} />
		</>
	)
);

function App() {
	useEffect(() => {
		setupLogoutSync();
	}, []);

	return (
		<AntApp>
			<AdminAuthProvider>
				<ProfileProvider>
					<SponsorProvider>
						<RouterProvider router={router} />
					</SponsorProvider>
				</ProfileProvider>
			</AdminAuthProvider>
		</AntApp>
	);
}

export default App;
